import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventory, api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { Plus, Upload, CheckCircle2, Clock, XCircle, Trash2 } from 'lucide-react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const STATUS_STYLES: Record<string, [string, React.ComponentType<any>]> = {
  pending:   ['badge-warn', Clock],
  claimed:   ['badge-info', CheckCircle2],
  consumed:  ['badge-up',   CheckCircle2],
  cancelled: ['badge-down', XCircle],
};

export default function Inventory() {
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => inventory.list({ per_page: 100 }),
  });

  return (
    <>
      <PageHeader
        title="Inventory"
        subtitle="pré-assignations serial → external_id"
        actions={
          <>
            <CleanupStaleButton />
            <button className="btn btn-sm" onClick={() => setBulkOpen(true)}>
              <Upload size={11} />Bulk import
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
              <Plus size={11} />Ajouter
            </button>
          </>
        }
      />

      <div className="flex-1 overflow-auto">
        <table className="luc-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Serial</th>
              <th>External ID</th>
              <th>Customer linked</th>
              <th>Plan</th>
              <th>Claimed at</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {data?.data.map((e) => {
              const [cls, Icon] = STATUS_STYLES[e.status] ?? ['badge', Clock];
              return (
                <tr key={e.id}>
                  <td>
                    <span className={`badge ${cls}`}>
                      <Icon size={10} />{e.status}
                    </span>
                  </td>
                  <td className="font-mono text-[12px]">{e.serial_number}</td>
                  <td className="font-mono text-[12px] text-text-muted">{e.external_id}</td>
                  <td>{e.customer?.name ?? <span className="text-text-faint italic">à venir</span>}</td>
                  <td className="text-text-muted">{e.service_plan_id ? `#${e.service_plan_id}` : '—'}</td>
                  <td className="font-mono text-[11px] text-text-muted">
                    {e.claimed_at ? new Date(e.claimed_at).toLocaleString() : '—'}
                  </td>
                  <td className="text-xs text-text-muted truncate max-w-xs">{e.notes ?? '—'}</td>
                </tr>
              );
            })}
            {!data?.data?.length && (
              <tr><td colSpan={7} className="text-center py-12 text-text-faint">Inventaire vide. Pré-assigne des serials.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateInventoryModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <BulkImportModal open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </>
  );
}

function CleanupStaleButton() {
  const qc = useQueryClient();
  const cleanup = useMutation({
    mutationFn: () => api.post<{ deleted: number }>('/inventory/cleanup-stale', { days: 30 }).then((r) => r.data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      alert(`${res.deleted} entries pending stales supprimées`);
    },
    onError: (e: Error) => alert('Erreur: ' + e.message),
  });
  return (
    <button
      className="btn btn-sm text-warning"
      onClick={() => {
        if (confirm('Supprimer les entries inventory "pending" sans device matché et plus vieilles que 30 jours?')) {
          cleanup.mutate();
        }
      }}
      disabled={cleanup.isPending}
    >
      <Trash2 size={11} />{cleanup.isPending ? 'Cleanup…' : 'Cleanup'}
    </button>
  );
}

function CreateInventoryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    serial_number: '', external_id: '', notes: '',
    wifi_ssid: '', wifi_psk: '',
    wifi_ssid_5g: '', wifi_psk_5g: '',
  });
  const create = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        serial_number: form.serial_number,
        external_id: form.external_id,
        notes: form.notes,
      };
      // Build prefill_config.wifi only if user filled anything
      const wifi: Record<string, string> = {};
      if (form.wifi_ssid) wifi.ssid = form.wifi_ssid;
      if (form.wifi_psk) wifi.psk = form.wifi_psk;
      if (form.wifi_ssid_5g) wifi.ssid_5g = form.wifi_ssid_5g;
      if (form.wifi_psk_5g) wifi.psk_5g = form.wifi_psk_5g;
      if (Object.keys(wifi).length) body.prefill_config = { wifi };
      return inventory.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      setForm({ serial_number: '', external_id: '', notes: '', wifi_ssid: '', wifi_psk: '', wifi_ssid_5g: '', wifi_psk_5g: '' });
      onClose();
    },
  });
  return (
    <Modal open={open} onClose={onClose} title="Pré-assigner un device" width="max-w-xl">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Serial number<span className="text-danger ml-0.5">*</span></span>
            <input className="input w-full font-mono" value={form.serial_number}
              onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
              placeholder="CXNK00654191" required />
          </label>
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">External ID<span className="text-danger ml-0.5">*</span></span>
            <input className="input w-full" value={form.external_id}
              onChange={(e) => setForm({ ...form, external_id: e.target.value })}
              placeholder="SONAR-4521" required />
          </label>
        </div>

        <div className="border-t border-border pt-3">
          <div className="text-xs text-text-muted mb-2 font-semibold">📡 Pré-configuration WiFi (optionnel)</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-text-faint block mb-1">SSID 2.4GHz / commun</span>
              <input className="input w-full font-mono text-xs" value={form.wifi_ssid}
                onChange={(e) => setForm({ ...form, wifi_ssid: e.target.value })}
                placeholder="MaisonClient" maxLength={32} />
            </label>
            <label className="block">
              <span className="text-xs text-text-faint block mb-1">Mot de passe (≥8 char)</span>
              <input type="text" className="input w-full font-mono text-xs" value={form.wifi_psk}
                onChange={(e) => setForm({ ...form, wifi_psk: e.target.value })}
                placeholder="WiFi1234567" minLength={8} maxLength={63} />
            </label>
          </div>
          <details className="mt-2">
            <summary className="text-xs text-text-faint cursor-pointer hover:text-text">SSID 5GHz séparé (avancé)</summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <label className="block">
                <span className="text-xs text-text-faint block mb-1">SSID 5GHz</span>
                <input className="input w-full font-mono text-xs" value={form.wifi_ssid_5g}
                  onChange={(e) => setForm({ ...form, wifi_ssid_5g: e.target.value })}
                  placeholder="MaisonClient-5G" maxLength={32} />
              </label>
              <label className="block">
                <span className="text-xs text-text-faint block mb-1">PSK 5GHz</span>
                <input type="text" className="input w-full font-mono text-xs" value={form.wifi_psk_5g}
                  onChange={(e) => setForm({ ...form, wifi_psk_5g: e.target.value })}
                  placeholder="(vide = même que 2.4G)" minLength={8} maxLength={63} />
              </label>
            </div>
          </details>
        </div>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Notes</span>
          <textarea className="input w-full h-16 py-2" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Installation prévue le 18/05…" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn">Annuler</button>
          <button type="submit" disabled={create.isPending} className="btn btn-primary">
            {create.isPending ? 'Création…' : 'Pré-assigner'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BulkImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('CXNK00111111,SONAR-100\nCXNK00222222,SONAR-101');
  const [result, setResult] = useState<{ created: number; updated: number; linked: number } | null>(null);

  const importMut = useMutation({
    mutationFn: () => {
      const entries = csv.trim().split('\n').map((line) => {
        const [serial_number, external_id, ...rest] = line.split(',').map((s) => s.trim());
        return { serial_number, external_id, notes: rest.join(',') || undefined };
      });
      return inventory.bulkImport(entries);
    },
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Bulk import inventory" width="max-w-2xl">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">
          Une entrée par ligne, format <code className="font-mono bg-bg-2 px-1 rounded">SERIAL,EXTERNAL_ID,notes</code> (notes optionnel)
        </p>
        <textarea
          className="input w-full h-48 py-2 font-mono text-xs"
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
        />
        {result && (
          <div className="card card-pad bg-success/5 border-success/30 text-sm">
            ✓ {result.created} créés · {result.updated} mis à jour · {result.linked} déjà liés à un customer
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn">Fermer</button>
          <button onClick={() => importMut.mutate()} disabled={importMut.isPending} className="btn btn-primary">
            {importMut.isPending ? 'Import…' : 'Importer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
